import { zodResolver } from '@hookform/resolvers/zod';
import { O } from '@mobily/ts-belt';
import { Button, CircularProgress, Grid, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'common/hooks/useTranslation';
import { Lambda } from 'common/types';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { usePropertyContext } from '../../../properties/components/SingleProperty';
import { useDeleteFireBreach } from '../../hooks/useDelete';
import { usePatchFireBreach } from '../../hooks/usePatch';
import { fireBreachSchema, FireBreachSchema } from '../../types/FireBreachSchema';
import { FireBreachDetails } from '../../types/ObjectDetails';
import { PhotosListWithUpload } from '../PhotosListWithUpload';

type Props = {
  details: FireBreachDetails;
  onSave: Lambda<void, void>;
  onDelete: Lambda<void, void>;
};

export const FireBreach: FC<Props> = ({ details, onSave, onDelete }) => {
  const { t } = useTranslation();
  const { id: propertyId } = usePropertyContext();
  const { location, text, id } = details;
  const { mutate: patchFireBreach, isLoading: isLoadingFireBreach } = usePatchFireBreach({
    onSuccess: () => {
      onSave();
      toast.success(t('general.updatedSuccessfully'));
    },
    onError: () => {
      toast.error(t('error.generalMessage'));
    },
  });
  const { mutate: deleteFireBreach, isLoading: isLoadingDeleteFireBreach } = useDeleteFireBreach({
    onSuccess: () => {
      onDelete();
      toast.success(t('general.deletedSuccessfully'));
    },
    onError: () => {
      toast.error(t('error.generalMessage'));
    },
  });

  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<FireBreachSchema>({
    defaultValues: {
      text: O.getWithDefault(text, ''),
      location: O.getWithDefault(location, ''),
    },
    mode: 'onChange',
    resolver: zodResolver(fireBreachSchema),
  });

  const [photosURLs, setPhotosURLs] = useState(details.photosURLs ?? []);

  const handleSave = (values: FireBreachSchema) => {
    patchFireBreach({ ...values, id, photosURLs });
  };

  const handleDelete = () => {
    deleteFireBreach(details.id);
  };

  return (
    <Stack component="form" onSubmit={handleSubmit(handleSave)}>
      <Typography sx={{ fontSize: 18, paddingBottom: 2 }}>{t('areas.fireBreach.label')}</Typography>
      <Stack gap={2} width="100%">
        <PhotosListWithUpload
          photosURLs={photosURLs}
          setPhotosUrls={setPhotosURLs}
          entityType="fireBreach"
          entityId={id}
          propertyId={O.getWithDefault(propertyId, 0)}
        />

        <TextField label={t('areas.fields.name')} {...register('text')} disabled />
        <TextField label={t('areas.fields.location')} {...register('location')} />
      </Stack>
      <Grid container justifyContent="space-between" mt={2}>
        <Button variant="text" color="error" onClick={handleDelete} disabled={isLoadingDeleteFireBreach}>
          {isLoadingDeleteFireBreach ? <CircularProgress size={16} /> : t('general.delete')}
        </Button>
        <Button variant="contained" color="primary" type="submit" disabled={!isValid || isLoadingFireBreach}>
          {isLoadingFireBreach ? <CircularProgress size={16} /> : t('general.save')}
        </Button>
      </Grid>
    </Stack>
  );
};
